import { concat, find, forEach, groupBy, includes } from "lodash";
import { TNumberOfKeyworks, countString } from "./stringHelper";
import moment from "moment";
import {
    KeywordsByDay,
    KeywordsByDayWithThematic,
    MainAnchorType,
    OtherAnchorType,
    Planning,
} from "../types/planning";
import { SiteTransport } from "../transports/site";
import { PlanningTransporter } from "../transports/planning";

export type TKeywordsByThematic = { thematicId: number; keyword: string };
export const getKeywordFromString = (str: string): Array<string> => {
    const keywords: string[] = [];

    const keywordsNotFormatted = str
        .replace(/[.,/#!$%^&*;:{}=`~()]/g, "")
        .replace(/\u200B/g, "")
        .split(/[\r\n]+/);

    keywordsNotFormatted.forEach((keyword) => {
        if (keyword !== "") keywords.push(keyword.trim());
    });

    return keywords;
};
export const getWebsiteFromString = (str: string): Array<string> => {
    const keywords: string[] = [];

    const keywordsNotFormatted = str
        .replace(/[,/#!$%^&*;:{}=`~()]/g, "")
        .replace(/\u200B/g, "")
        .split(/[\r\n]+/);

    keywordsNotFormatted.forEach((keyword) => {
        if (keyword !== "") keywords.push(keyword.trim());
    });

    return keywords;
};

export const getRandomKeywordsFromArray = (keywords: Array<String>, number: number) => {
    const shuffledArray = keywords.sort(() => 0.5 - Math.random());
    if (shuffledArray.length < number) {
        return shuffledArray.concat(shuffledArray.slice(0, number - shuffledArray.length));
    }
    return shuffledArray.slice(0, number);
};

export const getRandomArray = (array: Array<any>) => {
    return array.sort(() => 0.5 - Math.random());
};

const addDateToNLT1Keywords = (
    keywords: Array<KeywordsByDayWithThematic>,
    numberKeyword: number
) => {
    const today = moment().add(7, "d");
    const keywordsWithData: Array<KeywordsByDayWithThematic> = [];

    let duration = 0;

    forEach(keywords, (el) => {
        keywordsWithData.push({ ...el, date: today.add(duration, "d").format("DD/MM/YYYY") });
        const randomNumber = Math.floor(Math.random() * 3);
        const offset = randomNumber === 2 ? -1 : randomNumber;
        duration = Math.floor(30 / numberKeyword) + offset;
    });
    return keywordsWithData;
};

const getNLT1KeyWordsAlternate = (
    keywords: Array<KeywordsByDayWithThematic>,
    planning: Planning
): Array<KeywordsByDayWithThematic> => {
    const keywordsMixed: Array<KeywordsByDayWithThematic> = [];

    let par = 0;
    let odd = 1;

    const randomKeywords = getRandomArray(keywords);

    for (let i = 0; i <= randomKeywords.length - 1; i++) {
        let index = 0;
        if (randomKeywords[i].thematicId === planning.moneySite.thematic.id) {
            index = par;
            par += 2;
        } else {
            index = odd;
            odd += 2;
        }
        keywordsMixed[index] = randomKeywords[i];
    }

    return keywordsMixed;
};

//used In planningModalContainer
export const getRandomKeywordsFromStringByMonth = (
    keywords: string,
    numberKeyword: number,
    numberOfMonth: number
): Array<KeywordsByDay> => {
    const keywordsToArray = getKeywordFromString(keywords);
    const choosenKeyword = numberKeyword * numberOfMonth;
    const keywordsChoosen = getRandomKeywordsFromArray(keywordsToArray, choosenKeyword);
    const keywordsNotChoosen: Array<{ keyword: string }> = [];
    keywordsToArray.forEach((el) => {
        if (!includes(keywordsChoosen, el)) keywordsNotChoosen.push({ keyword: el });
    });
    const today = moment();
    const keywordByDate = keywordsChoosen.map((key) => {
        const duration = 30 / numberKeyword;
        const offset = Math.random() < 0.33 ? -1 : Math.random() > 0.66 ? 1 : 0;
        const addDuration = Math.floor(duration) + offset;
        return {
            keyword: key.toString(),
            date: today.add(addDuration <= 1 ? 1 : addDuration, "d").format("DD/MM/YYYY"),
        };
    });

    return [...keywordByDate, ...keywordsNotChoosen];
};

export const getRandomKeywordsFromArrayByThematic = (
    keywordsWithThematic: Array<TKeywordsByThematic>,
    thematicsNeeded: Array<number>,
    number: number,
    mainThematic: number
): TKeywordsByThematic[][] => {
    const otherKeywords: Array<TKeywordsByThematic> = [];
    const mainKeywords: Array<TKeywordsByThematic> = [];

    forEach(keywordsWithThematic, (el) => {
        if (el.thematicId === mainThematic) {
            mainKeywords.push(el);
        } else {
            otherKeywords.push(el);
        }
    });

    const differenceBetween = otherKeywords.length - Math.floor(number / 2);
    const numberOfMainThematicKeywords =
        differenceBetween >= 0
            ? Math.floor(number / 2)
            : Math.floor(number / 2) - differenceBetween;

    const mainRandom = getRandomArray(mainKeywords);
    const mainKeywordsShuffled: Array<TKeywordsByThematic> = mainRandom.slice(
        0,
        numberOfMainThematicKeywords
    );
    const mainKeywordNotUsed: Array<TKeywordsByThematic> = mainRandom.slice(
        numberOfMainThematicKeywords,
        mainRandom.length - 1
    );
    const otherRandom = getRandomArray(otherKeywords);
    const totalOtherKeywordsNeeded = number - numberOfMainThematicKeywords;
    const otherKeywordChoosed: Array<TKeywordsByThematic> = [];
    for (let i = 0; i < totalOtherKeywordsNeeded; i++) {
        let kw: TKeywordsByThematic;
        kw = find(
            otherRandom,
            (el) => !find(otherKeywordChoosed, (el2) => el2.keyword === el.keyword)
        );

        if (i < thematicsNeeded.length) {
            const kwNeeded = find(
                otherRandom,
                (el) =>
                    el.thematicId === thematicsNeeded[i] &&
                    !find(otherKeywordChoosed, (el2) => el2.keyword === el.keyword)
            );

            if (kwNeeded) {
                kw = kwNeeded;
            }
        }

        otherKeywordChoosed.push(kw);
    }
    const otherKeywordNotUsed = otherRandom.filter(
        (el) => !find(otherKeywordChoosed, (kw) => el.keyword === kw.keyword)
    );

    const keywordsNotUsed = mainKeywordNotUsed.concat(otherKeywordNotUsed);

    const currentKeyword: Array<TKeywordsByThematic> = [];
    forEach(mainKeywordsShuffled, (el, index) => {
        currentKeyword.push(el);
        if (otherKeywordChoosed.length - 1 >= index) {
            currentKeyword.push(otherKeywordChoosed[index]);
        }
    });

    return [currentKeyword, keywordsNotUsed];
};

export const getNLT1KeywordsByMonthWithThematics = async (
    planning: Planning,
    numberKeyword: number,
    keywords: { [thematic: number]: string[] }
): Promise<Array<KeywordsByDayWithThematic>> => {
    let mainIndex = 0;
    let otherIndex = 0;
    let keywordByThematic: Array<TKeywordsByThematic> = [];

    const keys = Object.keys(keywords);

    forEach(keys, (key: string) => {
        keywordByThematic = concat(
            keywordByThematic,
            keywords[parseInt(key, 10)].map((keyword) => ({
                thematicId: parseInt(key, 10),
                keyword,
            }))
        );
    });

    const checkNetlinkingKeywords = await PlanningTransporter.checkNetlinkingNumberOfKeyword(
        keywords,
        planning.id
    );

    if (!checkNetlinkingKeywords.ok) {
        throw new Error(checkNetlinkingKeywords.error);
    }

    const keywordByThematicId = groupBy(keywordByThematic, (el) => el.thematicId);
    const thematicIds = Object.keys(keywordByThematicId);
    const mainAnchorTypeShuffled = getRandomArray(MainAnchorType);
    const otherAnchorTypeShuffled = getRandomArray(OtherAnchorType);

    const nlT1KeywordsChosenByDay = await Promise.all(
        thematicIds.map(async (thematic): Promise<any> => {
            const rdChosen = await SiteTransport.findAvailableRDForT1ByThematic(
                planning.moneySite.id,
                parseInt(thematic, 10),
                keywordByThematicId[thematic].length
            );

            const keywordsWithRds = keywordByThematicId[thematic].map((el, index) => {
                let anchor = "";
                if (el.thematicId === planning.moneySite.thematic.id) {
                    anchor = `${mainAnchorTypeShuffled[mainIndex % mainAnchorTypeShuffled.length]}${
                        planning.moneySite.domain
                    }`;
                    mainIndex++;
                } else {
                    anchor = otherAnchorTypeShuffled[otherIndex % otherAnchorTypeShuffled.length];
                    otherIndex++;
                }

                const keywordWithRD = {
                    rd: rdChosen[index],
                    link: { anchor },
                    ...el,
                };

                return keywordWithRD;
            });

            return keywordsWithRds;
        })
    );

    const nlT1KeywordsChosenByDayFlat: Array<KeywordsByDayWithThematic> =
        nlT1KeywordsChosenByDay.flat();

    return addDateToNLT1Keywords(
        getNLT1KeyWordsAlternate(nlT1KeywordsChosenByDayFlat, planning),
        numberKeyword
    );
};

export const countKeyword = (keywords: string[], str: string): TNumberOfKeyworks => {
    let numberOfOccurrences: TNumberOfKeyworks = {};
    let newStr = str.replace(/<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g, " ");
    newStr = newStr.replace(/[.,/#!$%^&*;:{}=_`~()]/g, " ");
    newStr = newStr.replace(/[\u200B-\u200D\uFEFF]/g, "");
    newStr = newStr.replace(/[ ]{2,}/gi, " ");
    newStr = newStr.replace(/\n /, "\n");
    keywords?.forEach((el: string) => {
        numberOfOccurrences = {
            ...numberOfOccurrences,
            [el]: countString(`${el} `, newStr),
        };
    });

    return numberOfOccurrences;
};
